<template>
    <VForm ref="filterForm" @keyup.enter.native="onPressEnter">
        <VRow class="d-flex flex-direction-row mb-0 ml-0">
            <SrHeadline level="3" class="mr-4 advanced-filter-headline"> Filter </SrHeadline>
            <SrFilterLabel v-if="filterPredicate" v-model="filterPredicate" :config="config" />
        </VRow>
        <VRow class="d-flex justify-space-between pb-0 mt-0 ml-0">
            <VCol cols="4" class="pl-0 pb-0 pt-2">
                <SrSelect
                    v-model="filterProperty"
                    :items="availableProperties"
                    :rules="rules.emptyInputField"
                    :disabled="disabled"
                    item-value="name"
                    item-text="name"
                    label="Property"
                    return-object
                    attach
                    @change="selectDefaultOperator"
                />
            </VCol>
            <VCol cols="4" class="px-0 pt-2">
                <SrSelect
                    v-model="filterOperator"
                    :items="availableOperators"
                    :rules="rules.emptyInputField"
                    :disabled="!isPropertySelected"
                    label="Operator"
                    item-text="name"
                    item-value="identifier"
                    attach
                />
            </VCol>
            <VCol cols="4" class="pr-0 pt-2">
                <SrSelect
                    v-if="isTypeSelect"
                    v-model="filterValue"
                    :items="availableSelectValues"
                    :rules="rules.emptyInputField"
                    :disabled="!isPropertySelected || !isOperatorSelected"
                    :label="textFieldLabel"
                    :sort-order="filterProperty.selectSortOrder"
                    :searchable="filterProperty.selectSearchable"
                    attach
                    @change="onFilterChange"
                />
                <SrInput
                    v-else
                    v-model="filterValue"
                    :rules="rules.emptyInputField"
                    :disabled="!isPropertySelected || !isOperatorSelected"
                    :label="textFieldLabel"
                    :type="inputFieldType"
                />
            </VCol>
        </VRow>
        <VRow class="d-flex justify-space-between pb-0 mt-0 ml-0">
            <VCol cols="4" class="pl-0 pb-0 pt-0">
                <SrSearchInput
                    v-if="isQuickSearchEnabled"
                    v-model="quickSearchValue"
                    label="Quick search"
                    :debounce-ms="quickSearchDebounceMilliseconds"
                    @input="onQuickSearchChange"
                />
            </VCol>
        </VRow>
    </VForm>
</template>
<script>
import { Predicate } from '@ads/predicate-model';
import SrSelect from '../SrSelect/SrSelect';
import SrInput from '../SrInput/SrInput';
import SrSearchInput from '../SrSearchInput/SrSearchInput';
import SrHeadline from '../SrHeadline/SrHeadline';
import SrFilterLabel from './SrFilterLabel';
import { FILTER_PREDICATE_NAMES, OperatorTypes, SearchValueTypes } from './types';
import PropagateInputEvent from '../../mixins/PropagateInputEvent';
import getDefaultEmptyPredicate from './getDefaultEmptyPredicate';

export default {
    components: {
        SrSelect,
        SrInput,
        SrSearchInput,
        SrHeadline,
        SrFilterLabel,
    },
    mixins: [PropagateInputEvent],
    props: {
        /**
         * config: {
         *     properties: [{
         *         name: String,
         *         operatorType: enum -> OperatorTypes,
         *         searchValue: 'Select' or 'Text',
         *         selectValues?: Required if searchValue is Select -> Array of Strings,
         *         selectSortOrder?: enum -> SelectSortOrder,
         *         selectSearchable?: Boolean,
         *         toStringConfig?: {
         *              onlyValues? : boolean;
         *              getParameterValueLabel? : (predicate: Predicate) => string;
         *         },
         *     }],
         *     operators: OPERATORS -> const,
         *     quickSearch: {
         *        enabled: Boolean,
         *        fields: String[], -> Fields affected by quicksearch
         *        predicateDefinition: 'IS' | 'STARTS_WITH' ... -> Defaults to 'CONTAINS'
         *        debounceMs: Number, -> Defaults to 300ms
         *     },
         *     persistent: Boolean,
         * }
         */
        config: {
            type: Object,
            required: true,
        },
        /**
         * @model predicate
         * Must be a Predicate instance with the following minimal structure:
         * {
         *   type: 'COMPLEX',
         *   operator: 'AND',
         *   children: [
         *     {
         *       type: 'COMPLEX',
         *       name: FILTER_PREDICATE_NAMES.FILTER
         *     },
         *   ]
         * }
         */
        value: {
            type: Predicate,
            required: false,
            default: null,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            predicate: this.value ? this.value.clone() : getDefaultEmptyPredicate(),
            filterProperty: null,
            filterOperator: '',
            filterValue: '',
            quickSearchValue: '',
            rules: {
                emptyInputField: [(v) => !!v || 'This field is required'],
            },
        };
    },
    computed: {
        availableProperties() {
            return this.config.properties;
        },
        availableOperators() {
            if (!this.filterProperty) {
                return [];
            }
            const { operatorType } = this.filterProperty;
            return this.config.operators[operatorType] ?? [];
        },
        availableSelectValues() {
            return this.filterProperty?.selectValues ?? [];
        },
        inputFieldType() {
            if (!this.filterProperty) {
                return 'text';
            }
            const inputTypes = {
                [OperatorTypes.NUMERIC]: 'number',
                [OperatorTypes.ALPHABETIC]: 'text',
                [OperatorTypes.BOOLEAN]: 'text',
            };
            return inputTypes[this.filterProperty.operatorType];
        },
        isPredicateEmpty() {
            return !this.predicate.containsAtomic();
        },
        isPropertySelected() {
            if (!this.filterProperty) {
                return false;
            }
            return Boolean(this.filterProperty.name);
        },
        isOperatorSelected() {
            return Boolean(this.filterOperator);
        },
        isFilterInputEmpty() {
            return Boolean(!this.filterValue);
        },
        isQuickSearchInputEmpty() {
            return Boolean(!this.quickSearchValue);
        },
        isQuickSearchEnabled() {
            return Boolean(this.getQuickSearchConfig('enabled', false));
        },
        isTypeSelect() {
            if (!this.filterProperty) {
                return false;
            }
            return this.filterProperty.searchValueType === SearchValueTypes.SELECT;
        },
        filterPredicate: {
            get() {
                return this.predicate.findChild((predicate) => predicate.name === FILTER_PREDICATE_NAMES.FILTER);
            },
            set(newFilterPredicate) {
                if (this.filterPredicate) {
                    this.filterPredicate.remove();
                }
                this.predicate.appendChild(newFilterPredicate.clone());
                this.emitPredicateAndUpdateUrl();
            },
        },
        quickSearchPredicate() {
            return this.predicate.findChild((predicate) => predicate.name === FILTER_PREDICATE_NAMES.QUICK_SEARCH);
        },
        quickSearchFields() {
            return this.getQuickSearchConfig('fields', ['id', 'name']);
        },
        quickSearchPredicateDefinition() {
            return this.getQuickSearchConfig('predicateDefinition', 'CONTAINS');
        },
        quickSearchDebounceMilliseconds() {
            return this.getQuickSearchConfig('debounceMs', 300);
        },
        textFieldLabel() {
            if (!this.filterProperty) {
                return 'Value';
            }
            return this.filterProperty.name;
        },
    },
    watch: {
        value: {
            handler(newPredicate) {
                if (newPredicate) {
                    this.predicate = newPredicate.clone();
                    this.persistPredicateInUrl();
                }
            },
            deep: true,
        },
    },
    created() {
        const wasPredicateLoadedFromUrl = this.loadPredicateFromUrl();
        if (!wasPredicateLoadedFromUrl) {
            this.persistPredicateInUrl();
        }
    },
    methods: {
        applyFilterToPredicate() {
            if (this.isPropertySelected && this.isOperatorSelected && !this.isFilterInputEmpty) {
                this.filterPredicate.appendChild(this.createFilterPredicate());
                return true;
            }
            return false;
        },
        applyQuickSearchToPredicate() {
            const wasQuickSearchRemoved = this.removeQuickSearchFromPredicate();
            if (!this.isQuickSearchInputEmpty) {
                this.predicate.appendChild(this.createQuickSearchPredicate());
                return true;
            }
            return wasQuickSearchRemoved;
        },
        onPressEnter() {
            const wasFilterApplied = this.applyFilterToPredicate();
            const wasQuickSearchApplied = this.applyQuickSearchToPredicate();
            if (wasFilterApplied) {
                this.resetFilterForm();
            }
            if (wasFilterApplied || wasQuickSearchApplied) {
                this.emitPredicateAndUpdateUrl();
            }
        },
        onFilterChange() {
            const wasFilterApplied = this.applyFilterToPredicate();
            if (wasFilterApplied) {
                this.emitPredicateAndUpdateUrl();
                this.resetFilterForm();
            }
        },
        onQuickSearchChange() {
            const wasQuickSearchApplied = this.applyQuickSearchToPredicate();
            if (wasQuickSearchApplied) {
                this.emitPredicateAndUpdateUrl();
            }
        },
        emitPredicate() {
            this.emitInput(this.predicate.clone());
        },
        emitPredicateAndUpdateUrl() {
            this.persistPredicateInUrl();
            this.emitPredicate();
        },
        createFilterPredicate() {
            return new Predicate({
                type: 'ATOMIC',
                name: this.filterProperty.name,
                filterCriteria: this.filterProperty.filterCriteria,
                filterCriteriaId: this.filterProperty.filterCriteriaId,
                predicateDefinition: this.filterOperator,
                parameterValue: this.filterValue,
                toStringConfig: this.filterProperty.toStringConfig,
            });
        },
        createQuickSearchPredicate() {
            return new Predicate({
                name: FILTER_PREDICATE_NAMES.QUICK_SEARCH,
                type: 'COMPLEX',
                operator: 'OR',
                children: this.quickSearchFields.map(
                    (fieldName) =>
                        new Predicate({
                            type: 'ATOMIC',
                            name: `${FILTER_PREDICATE_NAMES.QUICK_SEARCH}-${fieldName}`,
                            filterCriteria: fieldName,
                            predicateDefinition: this.quickSearchPredicateDefinition,
                            parameterValue: this.quickSearchValue,
                        }),
                ),
            });
        },
        resetFilterForm() {
            this.filterProperty = null;
            this.filterOperator = '';
            this.filterValue = '';

            this.$refs.filterForm.resetValidation();
        },
        removeQuickSearchFromPredicate() {
            if (this.quickSearchPredicate) {
                this.quickSearchPredicate.remove();
                return true;
            }
            return false;
        },
        getQuickSearchConfig(key, fallback) {
            if (this.config.quickSearch && this.config.quickSearch[key]) {
                return this.config.quickSearch[key];
            }
            return fallback;
        },
        isNavigationRedundant(params) {
            return this.$route.fullPath === this.$router.resolve(params).route.fullPath;
        },
        getPredicateUrlParam() {
            return this.$router.currentRoute.query.filter;
        },
        removePredicateUrlParam() {
            const params = { query: { ...this.$route.query, filter: undefined } };
            if (!this.isNavigationRedundant(params)) {
                this.$router.push(params);
            }
        },
        loadPredicateFromUrl() {
            const predicateParam = this.getPredicateUrlParam();
            if (!predicateParam) {
                return false;
            }
            const decodedPredicateParam = atob(predicateParam.toString());
            this.predicate = Predicate.fromMinifiedJsonString(decodedPredicateParam);
            // Check if the quickSearch predicate exists
            if (this.quickSearchPredicate) {
                // Get a quickSearch atomic predicate
                const quickSearchAtomicPredicate = this.quickSearchPredicate.getAtomicPredicates().pop();
                if (quickSearchAtomicPredicate) {
                    // Set the appropriate value for the quick search
                    this.quickSearchValue = quickSearchAtomicPredicate.parameterValue;
                }
            }
            this.emitPredicate();
            return true;
        },
        persistPredicateInUrl() {
            if (!this.predicate || this.isPredicateEmpty) {
                this.removePredicateUrlParam();
                return;
            }
            if (!this.config.persistent) {
                return;
            }
            const encodedPredicate = btoa(this.predicate.toMinifiedJsonString());
            const params = { query: { ...this.$route.query, filter: encodedPredicate } };
            if (!this.isNavigationRedundant(params)) {
                this.$router.push(params);
            }
        },
        selectDefaultOperator() {
            if (this.availableOperators.length) {
                this.filterOperator = this.availableOperators[0].identifier;
            }
        },
    },
};
</script>
